import { normalizePackageSelection } from '@/lib/package-utils'
import { useCallback, useEffect, useRef } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { usePid2 } from '@/components/DropdownPid'
import { useCountry, useCountry2 } from '@/components/DropdownCountry'
import { usePackageName } from '@/components/DropdownPackage'
import { useDatePicker, useWeeklyDatePicker } from '@/components/MyDatePicker'
import {
  hasRoiUrlParams,
  parseRoiUrlParams,
  serializeRoiUrlParams,
} from './roi-url-params'

function isWeeklyOrMonthlyRoute(pathname) {
  return (
    pathname === '/roi/weekly' ||
    pathname.startsWith('/roi/weekly/') ||
    pathname === '/roi/monthly' ||
    pathname.startsWith('/roi/monthly/')
  )
}

function getDateStoreForRoute(pathname) {
  return isWeeklyOrMonthlyRoute(pathname) ? useWeeklyDatePicker : useDatePicker
}

function collectStateFromStores(pathname) {
  const pids = usePid2.getState().pids
  const countryValue = useCountry.getState().value
  const countriesMulti = useCountry2.getState().countries
  const packageName = usePackageName.getState().value
  const dateStore = getDateStoreForRoute(pathname)
  const { startDate, endDate } = dateStore.getState()

  const countries =
    countriesMulti.length > 0 ? countriesMulti : [countryValue]

  return { pids, countries, startDate, endDate, packageName }
}

function applyParsedToStores(parsed, pathname) {
  const { pids, countries, startDate, endDate, packageName } = parsed

  if (pids.length > 0) {
    usePid2.setState({ pids })
  }

  if (countries.length === 1) {
    useCountry.getState().update(countries[0])
    useCountry2.getState().setAll([countries[0]])
  } else if (countries.length > 1) {
    useCountry2.getState().setAll(countries)
    useCountry.getState().update(countries[0])
  }

  const effectivePids = pids.length > 0 ? pids : usePid2.getState().pids
  usePackageName.getState().update(normalizePackageSelection(effectivePids.join(','), packageName))

  const applyDates = (store) => {
    if (startDate) store.getState().updateStartDate(startDate)
    if (endDate) store.getState().updateEndDate(endDate)
  }

  applyDates(getDateStoreForRoute(pathname))
  applyDates(
    getDateStoreForRoute(pathname) === useWeeklyDatePicker
      ? useDatePicker
      : useWeeklyDatePicker,
  )
}

/** 首帧同步水合：在子组件 render 前将 URL 参数写入 store */
export function hydrateRoiStoresFromSearchParams(searchParams, pathname) {
  if (!hasRoiUrlParams(searchParams)) return
  applyParsedToStores(parseRoiUrlParams(searchParams), pathname)
}

export function useRoiUrlSync() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { pathname } = useLocation()

  const isInitialized = useRef(false)
  const isApplyingFromUrl = useRef(false)
  const isHydrating = useRef(true)
  const lastWrittenSearch = useRef('')
  const debounceTimer = useRef(null)
  const searchParamsRef = useRef(searchParams)
  const setSearchParamsRef = useRef(setSearchParams)
  const pathnameRef = useRef(pathname)

  searchParamsRef.current = searchParams
  setSearchParamsRef.current = setSearchParams
  pathnameRef.current = pathname

  const applyToStores = useCallback((parsed) => {
    isApplyingFromUrl.current = true
    applyParsedToStores(parsed, pathnameRef.current)
    queueMicrotask(() => {
      isApplyingFromUrl.current = false
    })
  }, [])

  const writeUrlFromStores = useCallback(() => {
    if (isApplyingFromUrl.current || isHydrating.current) return

    const path = pathnameRef.current
    const state = collectStateFromStores(path)
    const newParams = serializeRoiUrlParams(state)
    const serialized = newParams.toString()

    if (serialized === searchParamsRef.current.toString()) return

    lastWrittenSearch.current = serialized
    isApplyingFromUrl.current = true
    setSearchParamsRef.current(newParams, { replace: true })
    queueMicrotask(() => {
      isApplyingFromUrl.current = false
    })
  }, [])

  // URL → stores: init + browser navigation
  useEffect(() => {
    const current = searchParams.toString()

    if (!isInitialized.current) {
      isInitialized.current = true

      if (!hasRoiUrlParams(searchParams)) {
        const state = collectStateFromStores(pathname)
        const params = serializeRoiUrlParams(state)
        const serialized = params.toString()
        lastWrittenSearch.current = serialized
        isApplyingFromUrl.current = true
        setSearchParams(params, { replace: true })
        queueMicrotask(() => {
          isApplyingFromUrl.current = false
          isHydrating.current = false
        })
        return
      }

      lastWrittenSearch.current = current
      applyToStores(parseRoiUrlParams(searchParams))
      isHydrating.current = false
      return
    }

    if (current === lastWrittenSearch.current) return

    // Tab navigation may drop search briefly; restore via writeUrlFromStores
    if (current === '' && lastWrittenSearch.current) return

    lastWrittenSearch.current = current
    applyToStores(parseRoiUrlParams(searchParams))
  }, [searchParams, pathname, applyToStores, setSearchParams])

  // Tab switch: re-write URL from stores (NavLink may drop search without preserveSearch)
  useEffect(() => {
    if (!isInitialized.current || isHydrating.current) return
    writeUrlFromStores()
  }, [pathname, writeUrlFromStores])

  // stores → URL
  useEffect(() => {
    const scheduleSync = () => {
      if (isApplyingFromUrl.current || isHydrating.current) return
      clearTimeout(debounceTimer.current)
      debounceTimer.current = setTimeout(writeUrlFromStores, 100)
    }

    const unsubscribers = [
      usePid2.subscribe(scheduleSync),
      useCountry.subscribe(scheduleSync),
      useCountry2.subscribe(scheduleSync),
      usePackageName.subscribe(scheduleSync),
      getDateStoreForRoute(pathname).subscribe(scheduleSync),
    ]

    return () => {
      unsubscribers.forEach((unsub) => unsub())
      clearTimeout(debounceTimer.current)
    }
  }, [pathname, writeUrlFromStores])
}
